/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Loader } from "@nordcloud/gnui";
import { Breadcrumbs, showSuccess } from "~/components";
import { ABConfigurations } from "~/components/Applications/ApplicationDetails/ABConfigurations";
import { APPlans } from "~/components/Applications/ApplicationDetails/APPlans";
import { ApplicationDetailsInfo } from "~/components/Applications/ApplicationDetails/ApplicationDetailsInfo";
import { CloudAccounts } from "~/components/Applications/ApplicationDetails/CloudAccounts";
import { Environments } from "~/components/Applications/ApplicationDetails/Environments";
import { FCPatchPlans } from "~/components/Applications/ApplicationDetails/FCPPlans";
import { ApplicationInitialState } from "~/components/Applications/ApplicationDetails/initialState";
import { PDServices } from "~/components/Applications/ApplicationDetails/PDServices";
import { ServiceDescription } from "~/components/Applications/ApplicationDetails/ServiceDescription";
import { TabsPath } from "~/components/Customer/CustomerNavigation";
import { useShowError } from "~/components/Error/useShowError";
import { useUserConsumer } from "~/context/User/User";
import { Application, ApplicationRouteParams } from "~/models/application";
import { isStandardUser } from "~/routing";
import { generateActionSuccessText, useAxios } from "~/tools";
import { ApplicationViewCtx } from "~/views/customer/applications/ApplicationViewCtx";
import { ErrorModal } from "~/views/customer/applications/ErrorModal";

function ApplicationDetails() {
  const routeParams = useParams<ApplicationRouteParams>();
  const { applicationId } = routeParams;
  const [application, setApplication] = React.useState<Application>(
    ApplicationInitialState
  );
  const [isLoading, setIsLoading] = React.useState(true);
  const [errCode, setErrCode] = React.useState("");

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const fetchApplication = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/applications/${applicationId}`);
      setApplication(data);
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, applicationId, showError]);

  React.useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  const updateApplication = React.useCallback(
    async (data) => {
      try {
        await Axios().patch(`/applications/${application.id}`, data);
        showSuccess(
          generateActionSuccessText(`Application ${application.name}`)()(
            "updated"
          )()
        );
      } catch (err) {
        const popupErrors = [
          "INVALID_RUNBOOK_LOCATION_LINK",
          "INVALID_RUNBOOK_INCLUDE_LINK",
        ];
        if (popupErrors.includes(err.response.data.err_code)) {
          setErrCode(err.response.data.err_code);
        } else {
          showError(err.response);
        }
      }

      return fetchApplication();
    },
    [Axios, application, showError, fetchApplication]
  );

  const breadcrumbLabels = [
    { label: "Customers", uri: "/" },
    {
      uri: `/customers/${application?.customer?.id}/${TabsPath.INFO}`,
      label: application.customer.name,
    },
    {
      uri: `/customers/${application.customer.id}/applications`,
      label: "Applications",
    },
    { uri: application.id, label: application?.name },
  ];

  if (isLoading) {
    return (
      <Container css={{ width: "1200px" }}>
        <Loader inContent />
      </Container>
    );
  }

  return (
    <ApplicationViewCtx.Provider
      value={{ application, updateApplication, canEdit }}
    >
      <Container css={{ width: "1200px" }}>
        {application.name.length !== 0 && (
          <Breadcrumbs
            title={application.name || ""}
            customBreadcrumbs={breadcrumbLabels || [{ label: "", uri: "" }]}
            generalMapping={application.generalMapping}
          />
        )}
        <ApplicationDetailsInfo />
        <CloudAccounts />
        <Environments application={application} refetch={fetchApplication} />
        <PDServices />
        <ABConfigurations />
        <APPlans />
        <FCPatchPlans />
        <ServiceDescription />

        <ErrorModal errCode={errCode} close={() => setErrCode("")} />
      </Container>
    </ApplicationViewCtx.Provider>
  );
}

export { ApplicationDetails };
